import { ROUTE_ACCESS, DASHBOARD_ACCESS, BUTTON_PERMISSIONS, EXECUTIVE_DEPARTMENT } from '../configs/accessControl';
import { tryFeatureRouteGrants } from '../configs/featureAccess';

/**
 * Normalize employee data to extract roles and departments
 * @param {Object} employeeData - Employee data from API
 * @returns {Object} Normalized data with roles and departments arrays
 */
export const normalizeEmployeeAccess = (employeeData) => {
  if (!employeeData) {
    return { roles: [], departments: [] };
  }
  // Support both { data: { roles, departments } } and { roles, departments } at top level
  const data = employeeData.data || employeeData;

  const roles = (data.roles || []).map(role => ({
    id: role.id,
    title: role.title || role.role_title || ''
  }));

  const departments = (data.departments || []).map(dept => ({
    id: dept.id,
    title: dept.title || ''
  }));

  return { roles, departments };
};

/**
 * Normalize title for comparison (lowercase, trim)
 * @param {string} title - Title to normalize
 * @returns {string} Normalized title
 */
const normalizeTitle = (title) => {
  if (!title) return '';
  return String(title).trim().toLowerCase();
};

/**
 * Check if a value matches any in an array (case-insensitive)
 * @param {string} value - Value to check
 * @param {Array<string>} allowedValues - Array of allowed values
 * @returns {boolean} True if matches
 */
const matchesAny = (value, allowedValues) => {
  if (!allowedValues || allowedValues.length === 0) return true; // Empty array means all access
  const normalizedValue = normalizeTitle(value);
  return allowedValues.some(allowed => normalizeTitle(allowed) === normalizedValue);
};

/**
 * Check if employee has Executive department (all access)
 * @param {Array} departments - Employee departments array
 * @returns {boolean} True if has Executive department
 */
const hasExecutiveAccess = (departments) => {
  return departments.some(dept =>
    normalizeTitle(dept.title) === normalizeTitle(EXECUTIVE_DEPARTMENT)
  );
};

/**
 * Whether this caller is an executive — the same test the three access helpers
 * below short-circuit on, exposed for screens that branch on it rather than
 * merely gate on it.
 *
 * Bookings is the first: an executive there reaches every coach's calendar
 * regardless of who reports to whom, so the page shows them the whole tenant's
 * coaches instead of their own reporting tree.
 *
 * **This is a department title, not the backend's `Employees.is_executive`
 * column.** The booking API's `may_act_for` checks that column, and the two can
 * disagree. Callers must treat this as a painting decision only — every write
 * it leads to is still authorized server-side.
 *
 * @param {Object} employeeData - Employee data from API (either envelope).
 * @returns {boolean}
 */
export const isExecutiveEmployee = (employeeData) => {
  const { departments } = normalizeEmployeeAccess(employeeData);
  return hasExecutiveAccess(departments);
};

/**
 * Normalize a path by replacing UUIDs and numeric IDs with :id (for matching dynamic route config keys)
 * @param {string} path - Route path (e.g., '/recipe/abc-123')
 * @returns {string} Normalized path (e.g., '/recipe/:id')
 */
const normalizePathForMatching = (path) => {
  if (!path) return path;
  return path
    .replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '/:id')
    .replace(/\/\d+/g, '/:id');
};

/**
 * Normalize a config key so that any :param segment is treated as :id for prefix matching.
 * E.g. '/dashboard/:id/diet-plan/:planId' → '/dashboard/:id/diet-plan/:id'
 * @param {string} key - Route key from ROUTE_ACCESS
 * @returns {string} Normalized key
 */
const normalizeKeyForMatching = (key) => {
  if (!key) return key;
  return key.replace(/\/:[^/]+/g, '/:id');
};

/**
 * Get access rules for a path: exact match, then normalized (for :id), then longest parent prefix.
 * Uses normalized path and keys so that dynamic segments (e.g. :id, :planId) match correctly.
 * @param {string} path - Full path (e.g., '/recipe/uuid', '/dashboard/abc-123/diet-plan/xyz-456')
 * @returns {Object|undefined} Access rules from ROUTE_ACCESS or undefined
 */
const getRouteAccessRules = (path) => {
  if (!path) return undefined;
  // 1. Exact match
  if (ROUTE_ACCESS[path]) {
    return ROUTE_ACCESS[path];
  }
  // 2. Normalized path (dynamic segments like UUID or number)
  const normalized = normalizePathForMatching(path);
  if (normalized !== path && ROUTE_ACCESS[normalized]) {
    return ROUTE_ACCESS[normalized];
  }
  // 2b. Normalized path may use :id for all params; config may use :planId etc. Normalize keys for comparison.
  const normalizedPathForKeyMatch = normalized;
  for (const key of Object.keys(ROUTE_ACCESS)) {
    if (normalizeKeyForMatching(key) === normalizedPathForKeyMatch) {
      return ROUTE_ACCESS[key];
    }
  }
  // 3. Longest parent prefix: use normalized path and normalized keys so /dashboard/uuid/diet-plan/uuid matches /dashboard/:id/diet-plan/:planId
  const sortedKeys = Object.keys(ROUTE_ACCESS)
    .filter((key) => {
      if (key === '/') return false;
      const pathNorm = normalizedPathForKeyMatch;
      const keyNorm = normalizeKeyForMatching(key);
      return pathNorm === keyNorm || pathNorm.startsWith(keyNorm + '/');
    })
    .sort((a, b) => b.length - a.length);
  const parentKey = sortedKeys[0];
  return parentKey ? ROUTE_ACCESS[parentKey] : undefined;
};

/**
 * Check if user passes a single set of access rules (no Executive short-circuit).
 * @param {Object} accessRules - { departments, roles }
 * @param {Array} departments - User's departments
 * @param {Array} roles - User's roles
 * @returns {boolean}
 */
const passesAccessRules = (accessRules, departments, roles) => {
  if (!accessRules) return false;
  const hasDeptAccess = accessRules.departments.length === 0 ||
    departments.some(dept => matchesAny(dept.title, accessRules.departments));
  const hasRoleAccess = accessRules.roles.length === 0 ||
    roles.some(role => matchesAny(role.title, accessRules.roles));
  return hasDeptAccess && hasRoleAccess;
};

/**
 * Check if user has access to a route
 * Uses exact match, then normalized path, then parent prefix so that access to a sidebar section
 * (e.g. /recipe) grants access to all pages under it (e.g. /recipe/:id).
 * Feature cross-grants (e.g. diet plan editor when user can access recipes/templates) run via configs/featureAccess.
 * @param {string} route - Route path (e.g., '/hr/employees', '/recipe', '/recipe/abc-123')
 * @param {Object} employeeData - Employee data from context
 * @param {Array} departmentsWithRoles - Departments with roles from getCommonUtils
 * @returns {boolean} True if user has access
 */
export const hasRouteAccess = (route, employeeData, _departmentsWithRoles = []) => {
  if (!employeeData) {
    return false;
  }

  const { roles, departments } = normalizeEmployeeAccess(employeeData);

  // Executive department has all access
  if (hasExecutiveAccess(departments)) {
    return true;
  }

  const featureGrant = tryFeatureRouteGrants(route, {
    departments,
    roles,
    getRules: getRouteAccessRules,
    passesRules: passesAccessRules,
  });
  if (featureGrant) return true;

  // Get access rules (exact, normalized, or parent prefix)
  const accessRules = getRouteAccessRules(route);
  if (!accessRules) {
    return false;
  }

  return passesAccessRules(accessRules, departments, roles);
};

/**
 * Check if user has access to a dashboard
 * @param {string} dashboardId - Dashboard identifier (e.g., 'emp-dashboard')
 * @param {Object} employeeData - Employee data from context
 * @param {Array} departmentsWithRoles - Departments with roles from getCommonUtils
 * @returns {boolean} True if user has access
 */
export const hasDashboardAccess = (dashboardId, employeeData, _departmentsWithRoles = []) => {
  if (!employeeData) {
    return false;
  }

  const { roles, departments } = normalizeEmployeeAccess(employeeData);

  // Executive department has all access
  if (hasExecutiveAccess(departments)) {
    return true;
  }

  // Get access rules for this dashboard
  const accessRules = DASHBOARD_ACCESS[dashboardId];
  if (!accessRules) {
    // If dashboard not in config, deny access by default
    return false;
  }

  // Check department access
  const hasDeptAccess = accessRules.departments.length === 0 ||
    departments.some(dept => matchesAny(dept.title, accessRules.departments));

  // Check role access
  const hasRoleAccess = accessRules.roles.length === 0 ||
    roles.some(role => matchesAny(role.title, accessRules.roles));

  return hasDeptAccess && hasRoleAccess;
};

/**
 * Check if user has permission to perform a button action
 * @param {string} buttonId - Button identifier (e.g., 'verify-employee')
 * @param {string} dashboardId - Dashboard identifier (e.g., 'emp-dashboard')
 * @param {Object} employeeData - Employee data from context
 * @param {Array} departmentsWithRoles - Departments with roles from getCommonUtils
 * @returns {boolean} True if user has permission
 */
export const hasButtonPermission = (buttonId, dashboardId, employeeData, _departmentsWithRoles = []) => {
  if (!employeeData) {
    return false;
  }

  const { roles, departments } = normalizeEmployeeAccess(employeeData);

  // Executive department has all access
  if (hasExecutiveAccess(departments)) {
    return true;
  }

  // Get button permissions for this dashboard
  const dashboardPermissions = BUTTON_PERMISSIONS[dashboardId];
  if (!dashboardPermissions) {
    return false;
  }

  // Get access rules for this button
  const accessRules = dashboardPermissions[buttonId];
  if (!accessRules) {
    // If button not in config, deny access by default
    return false;
  }

  // Check department access
  const hasDeptAccess = accessRules.departments.length === 0 ||
    departments.some(dept => matchesAny(dept.title, accessRules.departments));

  // Check role access
  const hasRoleAccess = accessRules.roles.length === 0 ||
    roles.some(role => matchesAny(role.title, accessRules.roles));

  return hasDeptAccess && hasRoleAccess;
};
